import React, { useEffect, useState } from 'react';
import { getWeeklySummary, getOvertimeBalance, setOvertimeBalance } from '../services/timesheetService';

const OvertimeReport = ({ employees }) => {
  const now = new Date();
  const [employeeId, setEmployeeId] = useState(employees[0]?.id || '');
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previousBalance, setPreviousBalance] = useState(0);
  const [monthlyTotal, setMonthlyTotal] = useState(0);
  const [editedTotal, setEditedTotal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!employeeId) return;
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        const ym = new Date(year, month - 1, 1);
        const firstOfMonth = new Date(ym.getFullYear(), ym.getMonth(), 1);
        const lastOfMonth = new Date(ym.getFullYear(), ym.getMonth() + 1, 0);

        const normalizeDate = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

        const addDays = (d, days) => {
          const n = new Date(d);
          n.setDate(n.getDate() + days);
          return n;
        };

        const startOfWeekMonday = (d) => {
          const n = normalizeDate(d);
          const dow = n.getDay(); // 0..6 (Sun..Sat)
          const diffToMonday = (dow + 6) % 7; // Mon->0, Tue->1, ..., Sun->6
          return addDays(n, -diffToMonday);
        };

        const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

        // Même règle que backend: si fin du mois tombe Lun/Mar/Mer/Jeu => la semaine de fin de mois est affectée au mois suivant.
        const resolveOvertimeMonthForWeek = (monday) => {
          const start = normalizeDate(monday);
          const end = addDays(start, 6);
          const startYm = { y: start.getFullYear(), m: start.getMonth() + 1 };
          const endYm = { y: end.getFullYear(), m: end.getMonth() + 1 };
          if (startYm.y === endYm.y && startYm.m === endYm.m) return startYm;

          const eom = endOfMonth(start);
          const dow = eom.getDay(); // 0..6
          const shiftToNextMonth = dow >= 1 && dow <= 4; // Mon..Thu
          if (!shiftToNextMonth) return startYm;

          const next = new Date(eom.getFullYear(), eom.getMonth(), eom.getDate() + 1);
          return { y: next.getFullYear(), m: next.getMonth() + 1 };
        };

        const selectedYm = { y: year, m: month };

        // Générer les lundis des semaines candidates autour du mois, puis filtrer par mois d'affectation.
        const firstMonday = startOfWeekMonday(firstOfMonth);
        const lastMonday = startOfWeekMonday(lastOfMonth);
        const mondays = [];
        for (let cur = new Date(firstMonday); cur <= lastMonday; cur = addDays(cur, 7)) {
          const assigned = resolveOvertimeMonthForWeek(cur);
          if (assigned.y === selectedYm.y && assigned.m === selectedYm.m) {
            mondays.push(new Date(cur));
          }
        }

        // Formater la date en local pour éviter le décalage de jour dû au fuseau horaire
        const formatDate = (d) => {
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const weekResults = await Promise.all(
          mondays.map((monday) => {
            const assigned = resolveOvertimeMonthForWeek(monday);
            // Si la semaine est affichée dans le mois sélectionné mais que le lundi est dans le mois précédent,
            // on appelle l'API avec une date du mois affecté (ex: dimanche) pour éviter que le backend renvoie 0.
            const requestDate =
              assigned.y === selectedYm.y &&
              assigned.m === selectedYm.m &&
              (monday.getFullYear() !== selectedYm.y || monday.getMonth() + 1 !== selectedYm.m)
                ? addDays(monday, 6)
                : monday;
            return getWeeklySummary(employeeId, formatDate(requestDate)).then((res) => res.data);
          })
        );

        setWeeks(weekResults);

        const totalOvertime = weekResults.reduce(
          (sum, w) => sum + (w.overtimeWeekHours || 0),
          0
        );

        // Solde du mois précédent pour cet employé
        const prev = new Date(year, month - 2, 1); // month-1 previous, JS month indexé 0
        const prevYear = prev.getFullYear();
        const prevMonth = prev.getMonth() + 1;
        const balanceRes = await getOvertimeBalance(employeeId, prevYear, prevMonth);
        const prevBalance = parseFloat(balanceRes.data) || 0;
        setPreviousBalance(prevBalance);

        // Déterminer si les heures supplémentaires sont payées pour cet employé
        const emp = employees.find((e) => e.id === employeeId);
        const overtimePaid = !emp || emp.overtimePaid !== false;

        setMonthlyTotal(totalOvertime);
        const automaticTotal = overtimePaid
          ? totalOvertime
          : totalOvertime + prevBalance;
        setEditedTotal(automaticTotal.toFixed(2));
      } catch (e) {
        setError(
          e.response?.data?.error ||
            e.response?.data?.message ||
            e.message ||
            'Erreur lors du chargement des heures'
        );
        setWeeks([]);
        setPreviousBalance(0);
        setMonthlyTotal(0);
        setEditedTotal('');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [employeeId, year, month, employees]);

  const selectedEmployee = employees.find((e) => e.id === employeeId);
  const showPreviousBalance =
    selectedEmployee && selectedEmployee.overtimePaid === false;

  const handleSaveBalance = async () => {
    if (!employeeId) return;
    const value = parseFloat(editedTotal.replace(',', '.')) || 0;
    setSaving(true);
    setError('');
    try {
      await setOvertimeBalance(employeeId, year, month, value);
    } catch (e) {
      setError(
        e.response?.data?.error ||
          e.response?.data?.message ||
          e.message ||
          'Erreur lors de l\'enregistrement du solde'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <h3>Heures travaillées et heures supplémentaires par semaine</h3>

      <div className="form-row">
        <div className="form-group">
          <label>Employé</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value ? Number(e.target.value) : '')}
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.nom} {emp.prenom}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Année</label>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value || year, 10))}
            min="2020"
            max="2100"
          />
        </div>
        <div className="form-group">
          <label>Mois</label>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value || month, 10))}
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {new Date(2000, m - 1).toLocaleString('fr-FR', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>
      </div>

      {showPreviousBalance && (
        <div className="info-banner">
          Solde d'heures supplémentaires du mois précédent :{' '}
          <strong>{previousBalance.toFixed ? previousBalance.toFixed(2) : previousBalance} h</strong>
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}
      {loading && <div>Chargement...</div>}

      {!loading && weeks.length > 0 && (
        <table className="employee-table">
          <thead>
            <tr>
              <th>Semaine</th>
              <th>Heures travaillées</th>
              <th>Heures supplémentaires (règle 48h)</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, idx) => (
              <tr
                key={idx}
                className={
                  week.overtimeWeekHours && week.overtimeWeekHours > 0
                    ? 'overtime-row'
                    : ''
                }
              >
                <td>
                  {week.startOfWeek} - {week.endOfWeek}
                </td>
                <td>{week.workedWeekHours ?? 0}</td>
                <td>{week.overtimeWeekHours ?? 0}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                Total heures supplémentaires du mois :
              </td>
              <td>
                <input
                  type="number"
                  step="0.01"
                  value={editedTotal}
                  onChange={(e) => setEditedTotal(e.target.value)}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '0.8em', color: '#666' }}>
                  Calcul automatique : {monthlyTotal.toFixed(2)} h
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {!loading && !weeks.length && !error && (
        <div>Aucune donnée d'heures pour cette période.</div>
      )}

      <div className="form-actions" style={{ marginTop: '1rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveBalance}
          disabled={saving || !employeeId}
        >
          {saving ? 'Enregistrement...' : 'Enregistrer le solde du mois'}
        </button>
      </div>
    </div>
  );
};

export default OvertimeReport;

